import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { auth } from '../firebase/firebase';
import { Tile, mixTheme } from '../experience/illustrations';
import {
  LANGS, LANG_LABELS, subscribeAll, menuView, pick, fold, missingLangs, emptyI18n,
  importLegacy, setSoldOut, resetSoldOut, saveDish, newDishId, deleteDish, saveSection,
  saveMenu, createMenu, removeMenu, saveConfig, bulkAddDishes,
  subscribeReservations, setReservationStatus, updateReservation,
} from './model';
import { parseBulk } from './bulk';
import { confirmEnabled, sendConfirmationEmail } from '../experience/notify';
import { PlusIcon, CopyIcon, TrashIcon, PrintIcon, ExternalIcon, SearchIcon, StarIcon } from './icons';
import './menu.css';

const TABS = [
  { id: 'tonight', label: 'Bu Akşam' },
  { id: 'reservations', label: 'Rezervasyonlar' },
  { id: 'rep', label: 'Repertuvar' },
  { id: 'menus', label: 'Menüler' },
  { id: 'print', label: 'Baskı' },
  { id: 'settings', label: 'Ayarlar' },
];

const nameOf = (d) => pick(d.i18n, 'tr', 'name') || pick(d.i18n, 'fr', 'name') || '(adsız)';
const secTitle = (s) => pick(s.i18n, 'tr', 'title') || s.id;

const useAction = () => {
  const [busy, setBusy] = useState(false);
  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (e) { console.error(e); window.alert(`Kaydedilemedi: ${e.message}`); } finally { setBusy(false); }
  };
  return [busy, run];
};

// ---------------- Bu Akşam ----------------

const Tonight = ({ data }) => {
  const [busy, run] = useAction();
  const sections = menuView(data, data.config?.liveMenuId);
  const all = sections.flatMap((s) => s.dishes);
  const out = all.filter((d) => d.soldOut);
  const liveMenu = data.menus.find((m) => m.id === data.config?.liveMenuId);

  const reset = () => {
    if (!out.length) return;
    if (!window.confirm(`${out.length} tükendi işareti kaldırılacak. Yeni akşam için sıfırlansın mı?`)) return;
    run(() => resetSoldOut(out.map((d) => d.id)));
  };

  return (
    <>
      <div className="lm-card lm-row" style={{ marginBottom: 16 }}>
        <div className="lm-counter lm-grow">
          <div><b>{all.length - out.length}</b><span>Serviste</span></div>
          <div><b>{out.length}</b><span>Tükendi</span></div>
          <div><b>{all.length}</b><span>Toplam</span></div>
        </div>
        <div className="lm-note">Canlı menü: <b>{liveMenu?.name || '-'}</b></div>
        <button type="button" className="lm-btn" disabled={!out.length || busy} onClick={reset}>Yeni akşam için sıfırla</button>
      </div>
      <div className="lm-card">
        {sections.map((s) => (
          <div key={s.id}>
            <div className="lm-sec-title">{secTitle(s)}</div>
            <div className="lm-list">
              {s.dishes.map((d) => (
                <div key={d.id} className={`lm-item${d.soldOut ? ' is-out' : ''}`}>
                  <div className="lm-grow">
                    <div className="lm-item-name">{nameOf(d)}</div>
                  </div>
                  {d.soldOut && <span className="lm-pill lm-pill--out">Tükendi</span>}
                  <button
                    type="button"
                    role="switch"
                    className="lm-switch"
                    aria-checked={!!d.soldOut}
                    aria-label={`${nameOf(d)} tükendi`}
                    onClick={() => run(() => setSoldOut(d.id, !d.soldOut))}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
        {!sections.length && <div className="lm-note">Canlı menüde ürün yok.</div>}
      </div>
    </>
  );
};

// ---------------- Repertuvar ----------------

const DishEditor = ({ dish, sections, menus, onSaved }) => {
  const [draft, setDraft] = useState(dish.i18n);
  const [section, setSection] = useState(dish.section);
  const [busy, run] = useAction();
  useEffect(() => { setDraft(dish.i18n); setSection(dish.section); }, [dish]);

  const missing = missingLangs({ i18n: draft });
  const dirty = JSON.stringify(draft) !== JSON.stringify(dish.i18n) || section !== dish.section;
  const set = (l, f, v) => setDraft((p) => ({ ...p, [l]: { ...(p?.[l] || {}), [f]: v } }));

  const save = () => run(async () => { await saveDish(dish.id, { i18n: draft, section }); onSaved?.(); });
  const del = () => {
    if (!window.confirm(`"${nameOf(dish)}" repertuvardan ve tüm menülerden silinsin mi?`)) return;
    run(() => deleteDish(dish.id, menus));
  };

  return (
    <div className="lm-rep-dish">
      <div className="lm-row" style={{ marginBottom: 8 }}>
        <div className="lm-item-name lm-grow">{nameOf(dish)}</div>
        {missing.length > 0 && <span className="lm-pill lm-pill--warn">Eksik: {missing.map((l) => LANG_LABELS[l]).join(', ')}</span>}
        <select className="lm-field" style={{ width: 'auto' }} value={section} onChange={(e) => setSection(e.target.value)} aria-label="Bölüm">
          {sections.map((s) => <option key={s.id} value={s.id}>{secTitle(s)}</option>)}
        </select>
        <button type="button" className="lm-btn lm-btn--primary" disabled={!dirty || busy} onClick={save}>Kaydet</button>
        <button type="button" className="lm-btn lm-btn--ghost" disabled={busy} onClick={del} aria-label="Sil"><TrashIcon /></button>
      </div>
      <div className="lm-rep-grid">
        {LANGS.map((l) => (
          <div key={l}>
            <div className="lm-rep-lang">{LANG_LABELS[l]}</div>
            <input
              className={`lm-field${missing.includes(l) && !(draft?.[l]?.name || '').trim() ? ' is-missing' : ''}`}
              value={draft?.[l]?.name || ''}
              onChange={(e) => set(l, 'name', e.target.value)}
              placeholder="Ad"
            />
            <textarea
              className={`lm-field${missing.includes(l) && !(draft?.[l]?.description || '').trim() ? ' is-missing' : ''}`}
              value={draft?.[l]?.description || ''}
              onChange={(e) => set(l, 'description', e.target.value)}
              placeholder="Açıklama"
              rows={3}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const SectionTitles = ({ section }) => {
  const [draft, setDraft] = useState(section.i18n);
  const [show, setShow] = useState(section.showDescriptions !== false);
  const [busy, run] = useAction();
  useEffect(() => { setDraft(section.i18n); setShow(section.showDescriptions !== false); }, [section]);
  const dirty = JSON.stringify(draft) !== JSON.stringify(section.i18n) || show !== (section.showDescriptions !== false);
  return (
    <div className="lm-card" style={{ marginBottom: 10, background: '#fff' }}>
      <div className="lm-rep-grid">
        {LANGS.map((l) => (
          <div key={l}>
            <div className="lm-rep-lang">Bölüm adı {LANG_LABELS[l]}</div>
            <input className="lm-field" value={draft?.[l]?.title || ''}
              onChange={(e) => setDraft((p) => ({ ...p, [l]: { title: e.target.value } }))} />
          </div>
        ))}
      </div>
      <div className="lm-row" style={{ marginTop: 8 }}>
        <label className="lm-check lm-grow">
          <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} />
          <span>Misafir menüsünde açıklamaları göster</span>
        </label>
        <button type="button" className="lm-btn" disabled={!dirty || busy}
          onClick={() => run(() => saveSection(section.id, { i18n: draft, showDescriptions: show }))}>Bölümü kaydet</button>
      </div>
    </div>
  );
};

// ---------------- Toplu ekle ----------------

const BULK_HELP = `## Les Entrées
### Calamars Frits
TR: Türkçe açıklama
EN: English description
FR: Description française
ES: Descripción en español`;

const BulkAdd = ({ data, onClose }) => {
  const [text, setText] = useState('');
  const [busy, run] = useAction();
  const items = useMemo(() => (text.trim() ? parseBulk(text, data.sections) : []), [text, data.sections]);
  const existing = useMemo(() => new Set(data.dishes.map((d) => fold(pick(d.i18n, 'fr', 'name') || pick(d.i18n, 'en', 'name')))), [data.dishes]);
  const rows = items.map((it) => ({ ...it, dup: existing.has(fold(it.name)) }));
  const ok = rows.filter((r) => r.section && !r.dup);
  const live = data.menus.find((m) => m.id === data.config?.liveMenuId);
  const secName = (id) => secTitle(data.sections.find((s) => s.id === id) || { id });

  const add = () => {
    if (!ok.length) return;
    if (!window.confirm(`${ok.length} ürün eklenecek${live ? ` ve "${live.name}" menüsüne konacak` : ''}. Devam edilsin mi?`)) return;
    run(async () => {
      const n = await bulkAddDishes(ok, data.dishes, live);
      window.alert(`${n} ürün eklendi.`);
      onClose();
    });
  };

  return (
    <div className="lm-card" style={{ marginBottom: 16 }}>
      <div className="lm-row" style={{ marginBottom: 8 }}>
        <h2 className="lm-h2 lm-grow">Toplu ekle</h2>
        <button type="button" className="lm-btn lm-btn--ghost" onClick={onClose}>Kapat</button>
      </div>
      <p className="lm-note" style={{ marginTop: 0 }}>
        Bölüm başlığı <b>##</b>, ürün adı <b>###</b> ile başlar; altına TR, EN, FR, ES açıklamaları gelir.
        Sohbetten kopyalanan metin olduğu gibi yapıştırılabilir.
      </p>
      <textarea className="lm-field" rows={10} value={text} onChange={(e) => setText(e.target.value)} placeholder={BULK_HELP}
        style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 13 }} aria-label="Toplu ürün metni" />
      {rows.length > 0 && (
        <div className="lm-list" style={{ marginTop: 12 }}>
          {rows.map((r, i) => (
            <div key={i} className="lm-item">
              <div className="lm-grow">
                <div className="lm-item-name">{r.name}</div>
                <div className="lm-note">{r.section ? secName(r.section) : '-'}</div>
              </div>
              {r.dup && <span className="lm-pill lm-pill--out">Zaten var, atlanacak</span>}
              {!r.section && <span className="lm-pill lm-pill--out">{r.problems[0]}</span>}
              {r.section && !r.dup && r.problems.length > 0 && <span className="lm-pill lm-pill--warn">{r.problems.join(' / ')}</span>}
              {r.section && !r.dup && !r.problems.length && <span className="lm-pill lm-pill--live">Hazır</span>}
            </div>
          ))}
        </div>
      )}
      <div className="lm-row" style={{ marginTop: 12 }}>
        <span className="lm-note lm-grow">{rows.length ? `${ok.length} / ${rows.length} ürün eklenecek` : ''}</span>
        <button type="button" className="lm-btn lm-btn--primary" disabled={!ok.length || busy} onClick={add}>
          <PlusIcon /> {ok.length ? `${ok.length} ürünü ekle` : 'Ekle'}
        </button>
      </div>
    </div>
  );
};

const Repertoire = ({ data }) => {
  const [secFilter, setSecFilter] = useState('all');
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [q, setQ] = useState('');
  const [bulkOpen, setBulkOpen] = useState(false);
  const [busy, run] = useAction();

  const missingCount = data.dishes.filter((d) => missingLangs(d).length).length;
  const groups = data.sections
    .filter((s) => secFilter === 'all' || s.id === secFilter)
    .map((s) => ({
      ...s,
      dishes: data.dishes.filter((d) => d.section === s.id
        && (!onlyMissing || missingLangs(d).length)
        && (!q.trim() || fold(LANGS.map((l) => pick(d.i18n, l, 'name')).join(' ')).includes(fold(q.trim())))),
    }));

  const addDish = (sectionId) => run(async () => {
    const id = newDishId(sectionId);
    const order = Math.max(-1, ...data.dishes.filter((d) => d.section === sectionId).map((d) => d.order ?? 0)) + 1;
    await saveDish(id, { section: sectionId, order, soldOut: false, i18n: emptyI18n(['name', 'description']) });
    const live = data.menus.find((m) => m.id === data.config?.liveMenuId);
    if (live) await saveMenu(live.id, { dishIds: [...(live.dishIds || []), id] });
  });

  return (
    <>
      <div className="lm-card lm-row" style={{ marginBottom: 16 }}>
        <label className="lm-search lm-grow" style={{ minWidth: 200 }}>
          <SearchIcon />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ürün ara" />
        </label>
        <select className="lm-field" style={{ width: 'auto' }} value={secFilter} onChange={(e) => setSecFilter(e.target.value)} aria-label="Bölüm filtresi">
          <option value="all">Tüm bölümler</option>
          {data.sections.map((s) => <option key={s.id} value={s.id}>{secTitle(s)}</option>)}
        </select>
        <label className="lm-check">
          <input type="checkbox" checked={onlyMissing} onChange={(e) => setOnlyMissing(e.target.checked)} />
          <span>Çeviri eksik ({missingCount})</span>
        </label>
        <button type="button" className="lm-btn" onClick={() => setBulkOpen((v) => !v)}><PlusIcon /> Toplu ekle</button>
      </div>
      {bulkOpen && <BulkAdd data={data} onClose={() => setBulkOpen(false)} />}
      {groups.map((s) => (
        <div key={s.id} className="lm-card" style={{ marginBottom: 16 }}>
          <div className="lm-row">
            <h2 className="lm-h2 lm-grow">{secTitle(s)}</h2>
            <span className="lm-note">{s.dishes.length} ürün</span>
            <button type="button" className="lm-btn" disabled={busy} onClick={() => addDish(s.id)}><PlusIcon /> Ürün ekle</button>
          </div>
          <details style={{ margin: '10px 0' }}>
            <summary className="lm-note" style={{ cursor: 'pointer' }}>Bölüm adları ve görünüm</summary>
            <div style={{ marginTop: 8 }}><SectionTitles section={s} /></div>
          </details>
          {s.dishes.map((d) => <DishEditor key={d.id} dish={d} sections={data.sections} menus={data.menus} />)}
          {!s.dishes.length && <div className="lm-note" style={{ padding: '10px 0' }}>Bu filtrede ürün yok.</div>}
        </div>
      ))}
      <p className="lm-note">Yeni eklenen ürün canlı menüye de eklenir; diğer menülere Menüler sekmesinden ekleyin.</p>
    </>
  );
};

// ---------------- Menüler ----------------

const Menus = ({ data }) => {
  const [sel, setSel] = useState(data.config?.liveMenuId || data.menus[0]?.id);
  const [busy, run] = useAction();
  const menu = data.menus.find((m) => m.id === sel) || data.menus[0];
  const [name, setName] = useState(menu?.name || '');
  useEffect(() => { setName(menu?.name || ''); }, [menu?.id, menu?.name]);

  const ids = new Set(menu?.dishIds || []);
  const isLive = menu && menu.id === data.config?.liveMenuId;

  const toggle = (id) => run(() => saveMenu(menu.id, {
    dishIds: ids.has(id) ? menu.dishIds.filter((x) => x !== id) : [...(menu.dishIds || []), id],
  }));
  const toggleSection = (sid, on) => run(() => {
    const secIds = data.dishes.filter((d) => d.section === sid).map((d) => d.id);
    const next = on ? [...new Set([...(menu.dishIds || []), ...secIds])] : (menu.dishIds || []).filter((x) => !secIds.includes(x));
    return saveMenu(menu.id, { dishIds: next });
  });
  const create = () => {
    const n = window.prompt('Yeni menünün adı', 'Yeni menü');
    if (n) run(async () => setSel(await createMenu(n.trim())));
  };
  const duplicate = () => run(async () => setSel(await createMenu(`${menu.name} (kopya)`, menu.dishIds || [])));
  const del = () => {
    if (isLive) return window.alert('Canlı menü silinemez. Önce başka bir menüyü canlı yapın.');
    if (!window.confirm(`"${menu.name}" silinsin mi? Ürünler repertuvarda kalır.`)) return;
    run(async () => { await removeMenu(menu.id); setSel(data.config?.liveMenuId); });
  };

  const sortedMenus = [...data.menus].sort((a, b) => (a.id === data.config?.liveMenuId ? -1 : b.id === data.config?.liveMenuId ? 1 : (a.name || '').localeCompare(b.name || '', 'tr')));

  return (
    <div className="lm-split">
      <div className="lm-card">
        {sortedMenus.map((m) => (
          <button key={m.id} type="button" className={`lm-menu-btn${m.id === menu?.id ? ' is-sel' : ''}`} onClick={() => setSel(m.id)}>
            <span className="lm-grow">{m.name}</span>
            {m.id === data.config?.liveMenuId && <span className="lm-pill lm-pill--live">Canlı</span>}
            <span className="lm-note">{(m.dishIds || []).length}</span>
          </button>
        ))}
        <button type="button" className="lm-btn" style={{ marginTop: 10, width: '100%', justifyContent: 'center' }} disabled={busy} onClick={create}>
          <PlusIcon /> Yeni menü
        </button>
      </div>

      {menu && (
        <div className="lm-card">
          <div className="lm-row" style={{ marginBottom: 12 }}>
            <input className="lm-field lm-grow" style={{ fontFamily: 'var(--serif)', fontSize: 20 }} value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => name.trim() && name !== menu.name && run(() => saveMenu(menu.id, { name: name.trim() }))}
              aria-label="Menü adı" />
            {isLive
              ? <span className="lm-pill lm-pill--live">Canlı</span>
              : <button type="button" className="lm-btn lm-btn--primary" disabled={busy} onClick={() => run(() => saveConfig({ liveMenuId: menu.id }))}>Canlı yap</button>}
            <button type="button" className="lm-btn" disabled={busy} onClick={duplicate}><CopyIcon /> Çoğalt</button>
            <button type="button" className="lm-btn lm-btn--ghost" disabled={busy || isLive} onClick={del} aria-label="Menüyü sil"><TrashIcon /></button>
          </div>
          {data.sections.map((s) => {
            const secDishes = data.dishes.filter((d) => d.section === s.id);
            const n = secDishes.filter((d) => ids.has(d.id)).length;
            return (
              <div key={s.id}>
                <div className="lm-row lm-sec-title" style={{ fontSize: 17 }}>
                  <label className="lm-check lm-grow" style={{ padding: 0 }}>
                    <input type="checkbox" checked={secDishes.length > 0 && n === secDishes.length}
                      ref={(el) => { if (el) el.indeterminate = n > 0 && n < secDishes.length; }}
                      onChange={(e) => toggleSection(s.id, e.target.checked)} disabled={busy || !secDishes.length} />
                    <span>{secTitle(s)}</span>
                  </label>
                  <span className="lm-note">{n}/{secDishes.length}</span>
                </div>
                <div style={{ paddingLeft: 26 }}>
                  {secDishes.map((d) => (
                    <label key={d.id} className="lm-check">
                      <input type="checkbox" checked={ids.has(d.id)} onChange={() => toggle(d.id)} disabled={busy} />
                      <span>{nameOf(d)}</span>
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ---------------- Baskı ----------------

// A4 at 96dpi. The sheet shrinks its type (from 10.5pt down to 7pt) until the
// menu fits on one page; if it still does not fit it flows onto more pages.
const A4_PX = 1122;
const FONT_MAX = 10.5;
const FONT_MIN = 7;

// A row of mixed Mediterranean tiles across the top and bottom of the sheet.
const SheetFrieze = () => (
  <div className="lm-sheet-frieze" aria-hidden="true">
    {Array.from({ length: 40 }).map((_, i) => <Tile key={i} theme={mixTheme(i + 200)} dark />)}
  </div>
);

// `fluid` is the on-screen version (/menu): full width, no A4 auto-fit, and
// `sections` may come pre-filtered (search).
export const PrintSheet = React.forwardRef(({ data, menuId, lang, onFit, fluid = false, sections: given }, ref) => {
  const sections = given || menuView(data, menuId)
    .map((s) => ({ ...s, dishes: s.dishes.filter((d) => !d.soldOut) }))
    .filter((s) => s.dishes.length);
  const site = data.config?.i18n;
  const inner = useRef(null);
  // Web fonts change line heights; measure again once they have loaded.
  const [fontsReady, setFontsReady] = useState(false);
  useEffect(() => { document.fonts?.ready.then(() => setFontsReady(true)); }, []);
  const key = `${menuId}|${lang}|${sections.map((s) => `${s.id}:${s.dishes.map((d) => d.id).join(',')}`).join('|')}`;

  // Measure synchronously in one pass: start large, step down until it fits.
  useLayoutEffect(() => {
    const el = inner.current;
    if (!el || fluid) return;
    let f = FONT_MAX;
    el.style.fontSize = `${f}pt`;
    while (el.scrollHeight > A4_PX && f > FONT_MIN) {
      f = Math.round((f - 0.25) * 100) / 100;
      el.style.fontSize = `${f}pt`;
    }
    onFit?.({ font: f, pages: Math.max(1, Math.ceil(el.scrollHeight / A4_PX)) });
  }, [key, onFit, fontsReady, fluid]);

  const setRefs = (el) => {
    inner.current = el;
    if (typeof ref === 'function') ref(el); else if (ref) ref.current = el;
  };

  return (
    <div className={fluid ? 'lm-sheet lm-sheet--fluid' : 'lm-sheet'} ref={setRefs}>
      <SheetFrieze />
      <div className="lm-sheet-head">
        <div className="lm-emblem lm-emblem--sheet"><Tile theme="oxblood" dark /></div>
        <h1>{pick(site, lang, 'title') || 'LUCIEN14'}</h1>
        {pick(site, lang, 'subtitle') && <div className="lm-sheet-sub">{pick(site, lang, 'subtitle')}</div>}
        {pick(site, lang, 'date') && <div className="lm-date">{pick(site, lang, 'date')}</div>}
      </div>
      <div className="lm-sheet-cols">
        {sections.map((s) => {
          const described = s.showDescriptions !== false && s.dishes.some((d) => pick(d.i18n, lang, 'description'));
          return (
            <div key={s.id} id={fluid ? `sec-${s.id}` : undefined} className="lm-sheet-sec">
              <h2>{pick(s.i18n, lang, 'title')}</h2>
              {described
                ? s.dishes.map((d) => (
                  <div key={d.id} className="lm-sheet-dish">
                    <b>{pick(d.i18n, lang, 'name')}</b>
                    {pick(d.i18n, lang, 'description') && <div>{pick(d.i18n, lang, 'description')}</div>}
                  </div>
                ))
                : <p className="lm-sheet-inline">{s.dishes.map((d) => pick(d.i18n, lang, 'name')).filter(Boolean).join('  ·  ')}</p>}
            </div>
          );
        })}
      </div>
      {pick(site, lang, 'footer') && <div className="lm-sheet-foot">{pick(site, lang, 'footer')}</div>}
      <SheetFrieze />
    </div>
  );
});

const Print = ({ data }) => {
  const [lang, setLang] = useState('tr');
  const [menuId, setMenuId] = useState(data.config?.liveMenuId || data.menus[0]?.id);
  const [fit, setFit] = useState(null);
  // PrintSheet reports on every render; only store real changes.
  const onFit = React.useCallback((f) => setFit((p) => (p && p.font === f.font && p.pages === f.pages ? p : f)), []);
  const wrap = useRef(null);
  const sheet = useRef(null);
  const [scale, setScale] = useState(1);
  const [height, setHeight] = useState(null);

  // On screen the A4 sheet is scaled down to the available width (phones).
  useEffect(() => {
    const measure = () => {
      if (!wrap.current || !sheet.current) return;
      const s = Math.min(1, wrap.current.clientWidth / sheet.current.offsetWidth);
      setScale(s);
      setHeight(sheet.current.offsetHeight * s);
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (wrap.current) ro.observe(wrap.current);
    if (sheet.current) ro.observe(sheet.current);
    return () => ro.disconnect();
  }, []);

  return (
    <>
      <div className="lm-card lm-row lm-no-print" style={{ marginBottom: 16 }}>
        <select className="lm-field" style={{ width: 'auto' }} value={menuId} onChange={(e) => setMenuId(e.target.value)} aria-label="Menü">
          {data.menus.map((m) => <option key={m.id} value={m.id}>{m.name}{m.id === data.config?.liveMenuId ? ' (canlı)' : ''}</option>)}
        </select>
        <div className="lm-langs" style={{ margin: 0 }}>
          {LANGS.map((l) => (
            <button key={l} type="button" className="lm-lang" aria-pressed={l === lang} onClick={() => setLang(l)}>{LANG_LABELS[l]}</button>
          ))}
        </div>
        <span className="lm-note lm-grow">
          Tükenen ürünler baskıya girmez.
          {fit && (fit.pages > 1
            ? <> <b style={{ color: 'var(--red)' }}>Menü tek sayfaya sığmıyor, {fit.pages} sayfa basılacak.</b></>
            : <> Tek A4 sayfa.</>)}
        </span>
        <button type="button" className="lm-btn lm-btn--primary" onClick={() => window.print()}><PrintIcon /> Yazdır</button>
      </div>
      <div className="lm-sheet-wrap" ref={wrap} style={{ height: height ?? undefined }}>
        <div className="lm-sheet-scale" style={{ transform: `scale(${scale})` }}>
          <PrintSheet ref={sheet} data={data} menuId={menuId} lang={lang} onFit={onFit} />
        </div>
      </div>
    </>
  );
};

// ---------------- Rezervasyonlar ----------------

const STATUS = {
  new: { label: 'Yeni', cls: 'lm-pill--warn' },
  confirmed: { label: 'Onaylandı', cls: 'lm-pill--live' },
  cancelled: { label: 'İptal', cls: 'lm-pill--out' },
};
const trDate = (iso) => {
  const [y, m, d] = (iso || '').split('-');
  if (!d) return iso;
  const day = new Date(Number(y), Number(m) - 1, Number(d)).toLocaleDateString('tr-TR', { weekday: 'long' });
  return `${d}.${m}.${y} ${day}`;
};

const Reservations = ({ data }) => {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  const [showPast, setShowPast] = useState(false);
  const confirm = (r, resend = false) => run(async () => {
    if (!resend) await setReservationStatus(r.id, 'confirmed');
    if (!confirmEnabled() || !r.email) return;
    if (!resend && r.confirmMailAt) return;
    const note = window.prompt('Misafire not (isteğe bağlı). Örn: Masanız pencere kenarında.', r.hostNote || '');
    if (note === null) return; // cancelled: status stays confirmed, no email
    await sendConfirmationEmail(r, menuView(data, data.config?.liveMenuId), note);
    await updateReservation(r.id, { confirmMailAt: new Date().toISOString(), hostNote: note.trim() });
  });
  const [busy, run] = useAction();
  useEffect(() => subscribeReservations(setRows, (e) => setErr(e.message)), []);

  const today = new Date();
  const todayIso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const list = (rows || [])
    .filter((r) => showPast || (r.date || '') >= todayIso)
    .sort((a, b) => `${a.date} ${a.time}`.localeCompare(`${b.date} ${b.time}`));
  const fresh = (rows || []).filter((r) => r.status === 'new' && (r.date || '') >= todayIso).length;

  if (err) return <div className="lm-card">Rezervasyonlar okunamadı: {err}</div>;
  if (!rows) return <div className="lm-status">Yükleniyor…</div>;

  let lastDate = null;
  return (
    <>
      <div className="lm-card lm-row" style={{ marginBottom: 16 }}>
        <div className="lm-counter lm-grow">
          <div><b>{fresh}</b><span>Bekleyen</span></div>
          <div><b>{list.length}</b><span>{showPast ? 'Toplam' : 'Yaklaşan'}</span></div>
        </div>
        <label className="lm-check">
          <input type="checkbox" checked={showPast} onChange={(e) => setShowPast(e.target.checked)} />
          <span>Geçmişi de göster</span>
        </label>
      </div>
      <div className="lm-card">
        {!list.length && <div className="lm-note">Henüz rezervasyon yok.</div>}
        {list.map((r) => {
          const head = r.date !== lastDate ? <div key={`d-${r.date}`} className="lm-sec-title">{trDate(r.date)}</div> : null;
          lastDate = r.date;
          const st = STATUS[r.status] || STATUS.new;
          return (
            <React.Fragment key={r.id}>
              {head}
              <div className="lm-item" style={{ alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <div style={{ fontFamily: 'var(--serif)', fontSize: 20, minWidth: 58 }}>{r.time}</div>
                <div className="lm-grow" style={{ minWidth: 180 }}>
                  <div className="lm-item-name">{r.name} · {r.guests} kişi {r.type && <span className="lm-pill" style={{ background: r.type === 'brunch' ? '#fbecd0' : '#e4e8f0', marginLeft: 6 }}>{r.type === 'brunch' ? 'Brunch' : 'Akşam'}</span>}</div>
                  <div className="lm-note"><a href={`tel:${(r.phone || '').replace(/[^\d+]/g, '')}`}>{r.phone}</a>{r.email && <> · <a href={`mailto:${r.email}`}>{r.email}</a></>}{r.lang ? ` · ${r.lang.toUpperCase()}` : ''}</div>
                  {r.note && <div style={{ marginTop: 4 }}>{r.note}</div>}
                </div>
                <span className={`lm-pill ${st.cls}`}>{st.label}</span>
                {r.confirmMailAt && <span className="lm-pill lm-pill--live">E-posta gitti</span>}
                <div className="lm-row" style={{ gap: 6 }}>
                  {r.status !== 'confirmed' && <button type="button" className="lm-btn lm-btn--primary" disabled={busy} onClick={() => confirm(r)}>{confirmEnabled() && r.email ? 'Onayla ve e-posta gönder' : 'Onayla'}</button>}
                  {r.status === 'confirmed' && confirmEnabled() && r.email && <button type="button" className="lm-btn" disabled={busy} onClick={() => confirm(r, true)}>{r.confirmMailAt ? 'E-postayı tekrar gönder' : 'Onay e-postası gönder'}</button>}
                  {r.status !== 'cancelled' && <button type="button" className="lm-btn" disabled={busy} onClick={() => run(() => setReservationStatus(r.id, 'cancelled'))}>İptal</button>}
                  {r.status !== 'new' && <button type="button" className="lm-btn lm-btn--ghost" disabled={busy} onClick={() => run(() => setReservationStatus(r.id, 'new'))}>Geri al</button>}
                </div>
              </div>
            </React.Fragment>
          );
        })}
      </div>
      <p className="lm-note">Misafirler sitedeki formdan talep bırakır; arayıp teyit edince Onayla'ya bas.</p>
    </>
  );
};

// ---------------- Ayarlar ----------------

// Suggested section titles: Turkish and Spanish in their own languages
// instead of French everywhere.
const SECTION_NAMES = {
  'les-entr-es': { tr: 'Mezeler', es: 'Tapas', en: 'Small Plates', fr: 'Petites assiettes' },
  'les-plats-principaux': { tr: 'Ateşten', es: 'De la Brasa', en: 'From the Fire', fr: 'Au feu' },
  'les-desserts': { tr: 'Tatlılar', es: 'Dulces', en: 'Sweets', fr: 'Douceurs' },
  'les-cocktails': { tr: 'Kokteyller', es: 'Cócteles', en: 'Cocktails', fr: 'Cocktails' },
  'les-whiskies': { tr: 'Viskiler', es: 'Whiskies', en: 'Whisky', fr: 'Whiskies' },
  'les-gins': { tr: 'Cinler', es: 'Ginebras', en: 'Gin', fr: 'Gins' },
  'les-vins': { tr: 'Şaraplar', es: 'Vinos', en: 'Wine', fr: 'Vins' },
  'les-champagnes': { tr: 'Köpüklüler', es: 'Espumosos', en: 'Sparkling', fr: 'Bulles' },
  'les-boissons': { tr: 'Kahve & Çay', es: 'Café y Té', en: 'Coffee & Tea', fr: 'Café & Thé' },
};

const SectionRename = ({ sections }) => {
  const [busy, run] = useAction();
  const targets = sections.filter((s) => SECTION_NAMES[s.id]);
  const apply = () => {
    if (!window.confirm(`${targets.length} bölümün adı her dilde kendi diline çevrilecek (TR: Mezeler, Ateşten, Tatlılar... / ES: Tapas, De la Brasa, Dulces...). Devam edilsin mi?`)) return;
    run(async () => {
      for (const s of targets) {
        const n = SECTION_NAMES[s.id];
        // eslint-disable-next-line no-await-in-loop
        await saveSection(s.id, { i18n: Object.fromEntries(LANGS.map((l) => [l, { title: n[l] }])) });
      }
      window.alert('Bölüm adları güncellendi.');
    });
  };
  if (!targets.length) return null;
  return (
    <div className="lm-card" style={{ marginBottom: 16 }}>
      <h2 className="lm-h2" style={{ marginBottom: 8 }}>Bölüm adları</h2>
      <p className="lm-note" style={{ marginTop: 0 }}>
        Şu an bölümler her dilde Fransızca görünüyor ("Les Entrées"). Bu düğme her dile kendi adını verir:
        Türkçede Mezeler, Ateşten, Tatlılar; İspanyolcada Tapas, De la Brasa, Dulces gibi. Sonra Repertuvar'dan tek tek değiştirebilirsin.
      </p>
      <button type="button" className="lm-btn lm-btn--primary" disabled={busy} onClick={apply}>Önerilen adları uygula</button>
    </div>
  );
};


const Settings = ({ data }) => {
  const cfg = data.config || {};
  const [draft, setDraft] = useState(cfg.i18n || emptyI18n(['title', 'subtitle', 'date', 'footer']));
  const [busy, run] = useAction();
  useEffect(() => { if (cfg.i18n) setDraft(cfg.i18n); }, [cfg.i18n]);
  const dirty = JSON.stringify(draft) !== JSON.stringify(cfg.i18n);
  const FIELDS = [['title', 'Başlık'], ['subtitle', 'Alt başlık'], ['date', 'Tarih satırı'], ['footer', 'Alt not']];

  return (
    <>
      <SectionRename sections={data.sections} />
      <div className="lm-card" style={{ marginBottom: 16 }}>
        <h2 className="lm-h2" style={{ marginBottom: 8 }}>Misafir menüsü</h2>
        <label className="lm-check">
          <input type="checkbox" checked={!!cfg.hideSoldOut} disabled={busy} onChange={(e) => run(() => saveConfig({ hideSoldOut: e.target.checked }))} />
          <span>Tükenen ürünleri gizle (kapalıysa üstü çizili gösterilir)</span>
        </label>
        <label className="lm-check">
          <input type="checkbox" checked={cfg.showSearch !== false} disabled={busy} onChange={(e) => run(() => saveConfig({ showSearch: e.target.checked }))} />
          <span>Arama kutusunu göster</span>
        </label>
      </div>
      <div className="lm-card">
        <div className="lm-row" style={{ marginBottom: 10 }}>
          <h2 className="lm-h2 lm-grow">Başlık metinleri</h2>
          <button type="button" className="lm-btn lm-btn--primary" disabled={!dirty || busy} onClick={() => run(() => saveConfig({ i18n: draft }))}>Kaydet</button>
        </div>
        <div className="lm-rep-grid">
          {LANGS.map((l) => (
            <div key={l}>
              <div className="lm-rep-lang">{LANG_LABELS[l]}</div>
              {FIELDS.map(([f, label]) => (
                <input key={f} className="lm-field" style={{ marginBottom: 6 }} placeholder={label} aria-label={`${label} ${LANG_LABELS[l]}`}
                  value={draft?.[l]?.[f] || ''}
                  onChange={(e) => setDraft((p) => ({ ...p, [l]: { ...(p?.[l] || {}), [f]: e.target.value } }))} />
              ))}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

// ---------------- shell ----------------

const Kitchen = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState(() => {
    try { return localStorage.getItem('lm-admin-tab') || 'tonight'; } catch { return 'tonight'; }
  });
  const [busy, run] = useAction();

  useEffect(() => subscribeAll(setData, (e) => { console.error(e); setError(e); }), []);
  useEffect(() => { try { localStorage.setItem('lm-admin-tab', tab); } catch { /* ignore */ } }, [tab]);

  const legacy = data?.source === 'legacy';
  const body = useMemo(() => {
    if (!data || legacy) return null;
    switch (tab) {
      case 'rep': return <Repertoire data={data} />;
      case 'menus': return <Menus data={data} />;
      case 'print': return <Print data={data} />;
      case 'settings': return <Settings data={data} />;
      case 'reservations': return <Reservations data={data} />;
      default: return <Tonight data={data} />;
    }
  }, [data, legacy, tab]);

  const doImport = () => {
    if (!window.confirm('Mevcut menü (menu_translations) yeni yapıya kopyalanacak: bölümler, ürünler, "La Carte" menüsü ve ayarlar. Eski kayıt silinmez. Devam edilsin mi?')) return;
    run(async () => {
      const r = await importLegacy();
      window.alert(`${r.sections} bölüm ve ${r.dishes} ürün aktarıldı.`);
    });
  };

  return (
    <div className="lm">
      <div className="lm-awning lm-awning--thin lm-no-print" />
      <div className="lm-admin-top lm-no-print">
        <div className="lm-admin-bar">
          <h1><StarIcon size={14} /> LUCIEN14 Mutfak</h1>
          {!legacy && data && (
            <div className="lm-tabs" role="tablist">
              {TABS.map((t) => (
                <button key={t.id} type="button" role="tab" className="lm-tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)}>{t.label}</button>
              ))}
            </div>
          )}
          <Link className="lm-btn" to="/" target="_blank"><ExternalIcon /> Misafir menüsü</Link>
          <button type="button" className="lm-btn lm-btn--ghost" onClick={() => signOut(auth)}>Çıkış</button>
        </div>
      </div>
      <div className="lm-admin">
        {error && <div className="lm-card">Veri okunamadı: {error.message}</div>}
        {!error && !data && <div className="lm-status">Yükleniyor…</div>}
        {legacy && (
          <div className="lm-card" style={{ maxWidth: 640 }}>
            <h2 className="lm-h2" style={{ marginBottom: 8 }}>Yeni menü yapısına geçiş</h2>
            <p>
              Misafir sayfası şu an eski kayıttan okunuyor ({data.dishes.length} ürün, {data.sections.length} bölüm).
              Ürünleri bir kez repertuvara aktarınca "Bu Akşam", "Menüler" ve "Baskı" ekranları açılır.
            </p>
            <p className="lm-note">Eski kayıt olduğu gibi kalır; aktarım yalnızca yeni koleksiyonlara yazar.</p>
            <button type="button" className="lm-btn lm-btn--primary" disabled={busy} onClick={doImport}>Mevcut menüyü aktar</button>
          </div>
        )}
        {data?.source === 'empty' && <div className="lm-card">Firestore'da menü kaydı bulunamadı.</div>}
        {body}
      </div>
    </div>
  );
};

// ---------------- giriş ----------------

// Mutfak tek bir personel hesabıyla girer; ekranda yalnızca şifre sorulur.
// Hesap Firebase Console > Authentication'da bu e-postayla açılmalıdır.
export const KITCHEN_EMAIL = process.env.REACT_APP_MUTFAK_EMAIL || 'mutfak@lucien14.com';

const Login = () => {
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setBusy(true);
    setErr('');
    try {
      await signInWithEmailAndPassword(auth, KITCHEN_EMAIL, password);
    } catch (e2) {
      const code = e2.code || '';
      setErr(code.includes('too-many-requests')
        ? 'Çok fazla deneme yapıldı. Biraz bekleyip tekrar deneyin.'
        : code.includes('invalid-credential') || code.includes('wrong-password') || code.includes('user-not-found')
          ? 'Şifre hatalı.'
          : `Giriş yapılamadı (${code || e2.message}).`);
      setPassword('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="lm">
      <div className="lm-awning" />
      <div className="lm-status">
        <form className="lm-card" style={{ width: '100%', maxWidth: 360, textAlign: 'center' }} onSubmit={submit}>
          <div className="lm-emblem"><Tile theme="cobalt" /></div>
          <h1 style={{ fontSize: 26, marginBottom: 16 }}>LUCIEN14 Mutfak</h1>
          <input
            className="lm-field"
            type="password"
            autoComplete="current-password"
            autoFocus
            placeholder="Şifre"
            aria-label="Şifre"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {err && <div style={{ color: 'var(--red)', fontSize: 14, marginTop: 8 }} role="alert">{err}</div>}
          <button type="submit" className="lm-btn lm-btn--primary" style={{ width: '100%', justifyContent: 'center', marginTop: 12 }} disabled={busy || !password}>
            {busy ? 'Giriliyor…' : 'Giriş'}
          </button>
        </form>
      </div>
    </div>
  );
};

const MenuAdmin = () => {
  const [user, setUser] = useState(undefined);
  useEffect(() => onAuthStateChanged(auth, setUser), []);
  if (user === undefined) return <div className="lm"><div className="lm-status">Yükleniyor…</div></div>;
  return user ? <Kitchen /> : <Login />;
};

export default MenuAdmin;
